import type { ComputeUsingConfig, TableCalcFrame } from '../visualSpec'

function quoteIdent(id: string): string {
  const s = String(id ?? '')
  return `"${s.replaceAll('"', '""')}"`
}

function keyToSqlRef(key: string): string | null {
  const k = String(key || '').trim()
  if (!k) return null
  if (k.startsWith('sql:')) {
    const raw = k.slice(4).trim()
    return raw || null
  }
  const idx = k.lastIndexOf('.')
  if (idx <= 0 || idx >= k.length - 1) {
    return quoteIdent(k)
  }
  const sourceName = k.slice(0, idx)
  const fieldId = k.slice(idx + 1)
  if (!sourceName.trim() || !fieldId.trim()) return null
  return `${quoteIdent(sourceName)}.${quoteIdent(fieldId)}`
}

function renderFrame(frame: TableCalcFrame | null | undefined): string {
  if (!frame) return ''
  const type = frame.type === 'range' ? 'RANGE' : 'ROWS'

  const one = (v: number | null | undefined, isStart: boolean): string => {
    if (v === null || v === undefined) return isStart ? 'UNBOUNDED PRECEDING' : 'UNBOUNDED FOLLOWING'
    const n = Math.trunc(Number(v))
    if (!Number.isFinite(n)) return isStart ? 'UNBOUNDED PRECEDING' : 'UNBOUNDED FOLLOWING'
    if (n === 0) return 'CURRENT ROW'
    if (n < 0) return `${Math.abs(n)} PRECEDING`
    return `${n} FOLLOWING`
  }

  const start = one(frame.start, true)
  const end = one(frame.end, false)
  return ` ${type} BETWEEN ${start} AND ${end}`
}

export function renderOverClause(cfg: ComputeUsingConfig): { over: string; errors: string[] } {
  const errors: string[] = []
  const parts: string[] = []

  const partition = (cfg.partitioning || []).map(keyToSqlRef).filter(Boolean) as string[]
  const orderBy = (() => {
    const explicit = (cfg.order_by || []).map((o) => {
      const ref = keyToSqlRef(o.field)
      if (!ref) return null
      const dir = (o.direction === 'Desc') ? 'DESC' : 'ASC'
      return `${ref} ${dir}`
    }).filter(Boolean) as string[]
    if (explicit.length) return explicit

    // Default: if no explicit order, use addressing as ORDER BY ASC.
    const fromAddr = (cfg.addressing || []).map(keyToSqlRef).filter(Boolean) as string[]
    return fromAddr.map((r) => `${r} ASC`)
  })()

  if ((cfg.partitioning || []).length && partition.length === 0) {
    errors.push('Partitioning fields could not be resolved to SQL identifiers.')
  }
  if ((cfg.order_by || []).length && orderBy.length === 0) {
    errors.push('Order-by fields could not be resolved to SQL identifiers.')
  }

  if (partition.length) parts.push(`PARTITION BY ${partition.join(', ')}`)
  if (orderBy.length) parts.push(`ORDER BY ${orderBy.join(', ')}`)

  const frame = renderFrame(cfg.frame || null)
  if (frame) parts.push(frame.trim())

  return { over: parts.join(' '), errors }
}

